import withLog from './WithLog.react';
import {PropTypes} from 'prop-types';
import Event from '../../../utils/Event';

/**
 * Extends a Component Class with events for for Render, ComponentDidMount, and ComponentDidUpdate.
 * @param ComponentClass
 * @returns A new Component Class.
 */
function withLifecycleEvents(ComponentClass) {
    // eslint-disable-next-line react/display-name,react/no-multi-comp
    let propTypes = {
        onRender: PropTypes.arrayOf(PropTypes.func),
        onComponentDidUpdate: PropTypes.arrayOf(PropTypes.func),
        onComponentDidMount: PropTypes.arrayOf(PropTypes.func),
    };

    let eventComponentClass = class extends ComponentClass {
        constructor(props) {
            super(props);
            this.state = this.state || {};
            this.state = {
                ...this.state,
                onComponentDidMountEvent: new Event(),
                onComponentDidUpdateEvent: new Event(),
                onRenderEvent: new Event(),
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            let nextState = null;
            if (super.getDerivedStateFromProps) {
                nextState = super.getDerivedStateFromProps(nextProps, prevState);
            }
            let needsUpdate = false;
            for (let propName in propTypes) {
                if (nextProps[propName] !== prevState[propName]) {
                    needsUpdate = true;
                    break;
                }
            }
            if (needsUpdate) {
                nextState = nextState || {};
                // eslint-disable-next-line guard-for-in
                for (let propName in propTypes) {
                    nextState[propName] = nextProps[propName];
                    nextState[`${propName}Event`] = prevState[`${propName}Event`];
                    (prevState[propName] || []).forEach((callback) => {
                        prevState[`${propName}Event`].unsubscribe(callback);
                    });
                    (nextState[propName] || []).forEach((callback) => {
                        nextState[`${propName}Event`].subscribe(callback);
                    });
                }
            } else if (nextState) {
                nextState.onComponentDidMountEvent = prevState.onComponentDidMountEvent;
                nextState.onComponentDidUpdateEvent = prevState.onComponentDidUpdateEvent;
                nextState.onRenderEvent = prevState.onRenderEvent;
            }
            return nextState;
        }

        componentDidMount(): void {
            this.state.onComponentDidMountEvent.publish(this, {});
            if (super.componentDidMount) {
                super.componentDidMount();
            }
        }

        componentDidUpdate(prevProps, prevState, snapshot): void {
            this.state.onComponentDidUpdateEvent.publish(this, {});
            if (super.componentDidUpdate) {
                super.componentDidUpdate(prevProps, prevState, snapshot);
            }
        }

        render() {
            this.state.onRenderEvent.publish(this, {});
            if (super.render) {
                return super.render();
            }
            return null;
        }
    };

    eventComponentClass.propTypes = {
        ...ComponentClass.propTypes,
        ...propTypes,
    };
    eventComponentClass.displayName =
        ComponentClass.displayName || ComponentClass.name || 'Component';

    return withLog(eventComponentClass);
}

export default withLifecycleEvents;
